"use client";

import TournamentFilters from "./TournamentFilters";
import {tournamentYears,tournamentType} from "@/lib/tournament-filters";
import styles from "./TournamentGrid.module.css";
import Link from "next/link";
import { useMemo, useState } from "react";

import type { HeadToHeadMatch } from "@/lib/head-to-head";

type Props = {
  teams: { id: number; clubId?: number | null; name: string }[];
  matches: HeadToHeadMatch[];
  currentTournamentId: number;
};

export default function HeadToHead({ teams, matches, currentTournamentId }: Props) {
  const options = useMemo(() => {
    const clubs = new Map<number, { id: number; name: string }>();
    teams.forEach((team) => { if (team.clubId) clubs.set(team.clubId, { id: team.clubId, name: team.name }); });
    return [...clubs.values()];
  }, [teams]);
  const [first, setFirst] = useState(options[0]?.id ?? 0);
  const [second, setSecond] = useState(options[1]?.id ?? 0);
  const [scope, setScope] = useState("all");
  const [ageCategory, setAgeCategory] = useState("all");

  const [year,setYear]=useState("all");
  const [type,setType]=useState("all");
  const nameA = options.find((club) => club.id === first)?.name ?? "Команда 1";
  const nameB = options.find((club) => club.id === second)?.name ?? "Команда 2";
  const pairMatches = useMemo(() => matches.filter((game) =>
    ((game.homeClubId === first && game.awayClubId === second) ||
     (game.homeClubId === second && game.awayClubId === first)) &&
    (ageCategory === "all" || game.ageCategory === ageCategory) &&
    (year === "all" || tournamentYears(game.season).includes(year)) &&
    (type === "all" || tournamentType(game.tournamentName)===type)), [matches, first, second, ageCategory,year,type]);
  const competitions = useMemo(() => {
    const map = new Map<number, { id: number; name: string; season: string }>();
    pairMatches.forEach((game) => map.set(game.tournamentId, {
      id: game.tournamentId, name: game.tournamentName, season: game.season,
    }));
    return [...map.values()];
  }, [pairMatches]);
  const meetings = pairMatches.filter((game) => scope === "all" || String(game.tournamentId) === scope);
  const summary = meetings.reduce((total, game) => {
    const a = game.homeClubId === first ? game.homeGoals : game.awayGoals;
    const b = game.homeClubId === first ? game.awayGoals : game.homeGoals;
    total.aGoals += a;
    total.bGoals += b;
    if (a > b) total.aWins += 1;
    else if (a < b) total.bWins += 1;
    else total.draws += 1;
    return total;
  }, { aWins: 0, bWins: 0, draws: 0, aGoals: 0, bGoals: 0 });

  return (
    <section className="space-y-4" aria-label="Очные встречи клубов" data-testid="head-to-head">
      <div className="glass-card rounded-2xl p-5">
        <div className="mb-5">
          <h2 className="text-lg font-black uppercase tracking-wide text-white">Очные встречи</h2>
          <p className="mt-1 text-sm text-emerald-200/55">История клубов во всех сохранённых турнирах. Выберите двух соперников.</p>
        </div>
        <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <label className="min-w-0 text-xs font-bold uppercase tracking-wider text-emerald-200/60">
            Первая команда
            <select aria-label="Первая команда" value={first} onChange={(event) => { setFirst(Number(event.target.value)); setScope("all"); }} className="mt-2 w-full rounded-xl border border-emerald-400/25 bg-[#07251a] px-3 py-3 text-sm font-semibold normal-case text-white outline-none focus:border-emerald-300">
              {options.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
            </select>
          </label>
          <span className="hidden h-11 items-center px-3 text-sm font-black tracking-widest text-emerald-300/70 sm:inline-flex" aria-hidden="true">VS</span>
          <label className="min-w-0 text-xs font-bold uppercase tracking-wider text-emerald-200/60">
            Вторая команда
            <select aria-label="Вторая команда" value={second} onChange={(event) => { setSecond(Number(event.target.value)); setScope("all"); }} className="mt-2 w-full rounded-xl border border-emerald-400/25 bg-[#07251a] px-3 py-3 text-sm font-semibold normal-case text-white outline-none focus:border-emerald-300">
              {options.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
            </select>
          </label>
        </div>
        {first === second ? (
          <p className="mt-4 rounded-xl bg-amber-400/10 px-4 py-3 text-sm text-amber-100" role="status">Выберите две разные команды.</p>
        ) : (
          <dl className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: nameA, value: summary.aWins, note: "побед", color: "text-emerald-300" },
              { label: "Ничьи", value: summary.draws, note: `${meetings.length} матчей`, color: "text-amber-200" },
              { label: nameB, value: summary.bWins, note: "побед", color: "text-emerald-300" },
            ].map((item, index) => (
              <div key={index} className="min-w-0 rounded-xl bg-emerald-950/50 px-2 py-4 text-center ring-1 ring-emerald-400/10 sm:px-4">
                <dt className="min-h-8 break-words text-xs font-semibold text-emerald-100/60 sm:min-h-5">{item.label}</dt>
                <dd className={`tabular mt-1 text-3xl font-black ${item.color}`}>{item.value}</dd>
                <dd className="mt-1 text-[11px] text-emerald-200/45">{item.note}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {first !== second ? (
        <div className="glass-card overflow-hidden rounded-2xl">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-emerald-400/15 p-5">
            <div>
              <h3 className="text-base font-black uppercase tracking-wide text-white">История встреч <span className="text-emerald-300">({meetings.length})</span></h3>
              <p className="mt-1 text-xs text-emerald-200/55">Суммарный счёт: {summary.aGoals} : {summary.bGoals}</p>
            </div>
            <div className="w-full"><TournamentFilters fields={[
              {label:"Возраст",value:ageCategory,options:[...new Set(matches.map(g=>g.ageCategory))].sort(),onChange:v=>{setAgeCategory(v);setScope("all");}},
              {label:"Год",value:year,options:[...new Set(matches.flatMap(g=>tournamentYears(g.season)))].sort((a,b)=>Number(b)-Number(a)),onChange:v=>{setYear(v);setScope("all");}},
              {label:"Тип турнира",value:type,options:[...new Set(matches.map(g=>tournamentType(g.tournamentName)))].sort(),onChange:v=>{setType(v);setScope("all");}},
            ]} onReset={()=>{setAgeCategory("all");setYear("all");setType("all");setScope("all");}} /></div>
            <label className="flex w-full min-w-0 flex-col gap-2 text-xs font-semibold text-emerald-200/65 sm:w-auto sm:flex-row sm:items-center">
              Турнир
              <select aria-label="Турнир" value={scope} onChange={(event) => setScope(event.target.value)} className={styles.filterSelect}>
                <option value="all">Все турниры</option>
                {competitions.map((competition) => (
                  <option key={competition.id} value={competition.id}>{competition.name} {pairMatches.find((game) => game.tournamentId === competition.id)?.ageCategory || "40+"} · {competition.season || "без сезона"}</option>
                ))}
              </select>
            </label>
          </div>
          {meetings.length ? (
            <ul className="divide-y divide-emerald-400/10" aria-label="Результаты очных встреч">
              {meetings.map((game) => (
                <li key={game.id} className="h2h-result transition hover:bg-emerald-400/5" data-match-id={game.id}>
                  <Link href={`/tournaments/${game.tournamentSlug}`} className="h2h-tournament group rounded-md focus-visible:outline-2 focus-visible:outline-emerald-300" data-tournament-id={game.tournamentId}>
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-emerald-300/70">Сезон {game.season || "не указан"}</span>
                    <span className="block font-semibold leading-snug text-emerald-100/80 group-hover:text-emerald-300 group-hover:underline">{game.tournamentName} {game.ageCategory} <span aria-hidden="true">↗</span></span>
                    {game.detailsLabel ? (
                      <span className="mt-1 block text-[10px] text-emerald-200/40">{game.detailsLabel}</span>
                    ) : null}
                  </Link>
                  <div className="h2h-scoreline">
                    <span className={`min-w-0 break-words text-right ${game.homeGoals > game.awayGoals ? "font-bold text-white" : "text-emerald-100/70"}`}>{game.home}</span>
                    <a href={`/tournaments/${game.tournamentSlug}#match-${game.id}`} className="tabular whitespace-nowrap rounded-lg bg-emerald-950/80 px-2.5 py-2 text-center font-black text-emerald-300 ring-1 ring-emerald-400/20 transition hover:bg-emerald-400/15 focus-visible:outline-2 focus-visible:outline-emerald-300 sm:px-4" aria-label={`Открыть матч ${game.home} — ${game.away}, ${game.homeGoals}:${game.awayGoals}`}>
                      {game.homeGoals} : {game.awayGoals}
                    </a>
                    <span className={`min-w-0 break-words ${game.awayGoals > game.homeGoals ? "font-bold text-white" : "text-emerald-100/70"}`}>{game.away}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="px-5 py-10 text-center text-sm text-emerald-200/50">Встреч выбранных команд пока нет.</p>}
        </div>
      ) : null}
    </section>
  );
}
